import { Nullable } from '@shared-types/util'

import { Dayjs, dayjs } from './dayjs-plugins'
import { displayTextOrPlaceholder } from './text'

export const LONG_DATE_FORMAT = 'dddd, MMMM DD, YYYY'
export const LONG_DATE_TIME_FORMAT = 'dddd, MMMM DD, YYYY HH:mm'
export const ITALIAN_LONG_DATE_FORMAT = 'dddd, DD MMMM, YYYY'
export const ITALIAN_LONG_DATE_TIME_FORMAT = 'dddd, DD MMMM, YYYY HH:mm'
export const DEFAULT_DATE_FORMAT = 'DD-MMM-YYYY'
export const DEFAULT_ITALIAN_DATE_FORMAT = 'DD/MM/YYYY'
export const DEFAULT_ITALIAN_DATE_TIME_FORMAT = 'DD/MM/YY HH:mm'
export const DEFAULT_DATETIME_FORMAT = 'DD-MMM-YYYY HH:mm'
export const DEFAULT_TIME_FORMAT = 'HH:mm'
export const FULL_DATE_TIME_FORMAT = 'ddd DD/MM, HH:mm'
export const DEFAULT_DATETIME_FORMAT_V2 = 'DD-MMM-YYYY | HH:mm'

export const DISPLAY_DATETIME_FORMAT = 'HH:mm [on] DD-MMM-YYYY'
export const DISPLAY_DATETIME_FORMAT_IT = 'HH:mm [del] DD-MMM-YYYY'

export const getDisplayDatetimeFormat = (locale?: string) => {
  if (locale === 'it') {
    return DISPLAY_DATETIME_FORMAT_IT
  }
  return DISPLAY_DATETIME_FORMAT
}

export function isWeekend(dayjsObj: Dayjs) {
  return dayjsObj.day() === 0 || dayjsObj.day() === 6
}

export const getUtcTime = (time?: Dayjs, formatOverride: string = DEFAULT_DATETIME_FORMAT) => {
  if (time) {
    return dayjs.utc(time).format(formatOverride)
  }
  return dayjs.utc().format(formatOverride)
}

export const getUtcDayjs = (time?: Nullable<string>) => {
  return dayjs.utc(time)
}

export const formatAsUTCTime = (time: string) => {
  return dayjs.utc(time).format(DEFAULT_TIME_FORMAT)
}

export const formatAsUTCDate = (time: string, formatOverride: string = DEFAULT_DATE_FORMAT) => {
  return dayjs.utc(time).format(formatOverride)
}

export const formatAsUTCDatetime = (
  time: string,
  formatOverride: string = DEFAULT_DATETIME_FORMAT
) => {
  return dayjs.utc(time).format(formatOverride)
}

// Local dates can be used to display times such as createdAt etc
// We should not use them for bookings. Bookings should be universally in UTC
export const formatLocalDateTime = (
  time: string,
  formatOverride: string = DEFAULT_DATETIME_FORMAT
) => {
  return dayjs.utc(time).local().format(formatOverride)
}

export const getLocalDayjs = (time?: Nullable<string>) => {
  return dayjs.utc(time).local()
}

// Local dates can be used to display times such as createdAt etc
// We should not use them for bookings. Bookings should be universally in UTC
export const formatLocalDate = (time: string, formatOverride: string = DEFAULT_DATE_FORMAT) => {
  return dayjs.utc(time).local().format(formatOverride)
}

export const safeFormatDayjs = (m: Nullable<Dayjs | string>, format: string) => {
  if (!m || typeof m === 'string') {
    return dayjs(m).format(format)
  }
  return m.format(format)
}

export const formatDayjstUTC = (m: Dayjs, format: string) => {
  return safeFormatDayjs(dayjs.utc(m), format)
}

export const nearestMinute = (_dayjs: Dayjs, interval: number) => {
  const remainder = interval - (_dayjs.minute() % interval)

  return dayjs(_dayjs).add(remainder, 'minutes')
}

export const nearest15Minute = (_dayjs: Dayjs) => {
  const interval = 15
  return nearestMinute(_dayjs, interval)
}

export const currentISODate = () => {
  return safeFormatDayjs(getUtcTime(), DEFAULT_DATE_FORMAT)
}

export const getFirstHourOfDate = (m: Dayjs) => {
  return {
    start_time: dayjs(m).startOf('day').toISOString(),
    end_time: dayjs(m).startOf('day').add(1, 'hours').toISOString(),
  }
}

export const getFirstHourOfDateTime = (m: Dayjs) => {
  return {
    start_time: dayjs(m).startOf('minute').toISOString(),
    end_time: dayjs(m).add(1, 'hours').toISOString(),
  }
}

export const getISOStringWithoutTime = (m: Dayjs | Date) => {
  return m.toISOString().split('T')[0]
}

export const getISOStringWithoutTimeFromDateString = (dateString: string) => {
  return dateString.split('T')[0]
}

export const getDateLocale = (nextLocale?: string) => {
  if (nextLocale === 'it') {
    return 'it-IT'
  }
  return 'en-GB'
}

export const displayDateOrPlaceholder = (dateStr?: Nullable<string>) => {
  return dateStr
    ? dayjs(dateStr).format(DEFAULT_DATE_FORMAT)
    : (displayTextOrPlaceholder(null) as string)
}

export const displayUTCDateOrPlaceholder = (dateStr?: Nullable<string>) => {
  return dateStr
    ? dayjs.utc(dateStr).format(DEFAULT_DATE_FORMAT)
    : (displayTextOrPlaceholder(null) as string)
}

export const displayDateTimeOrPlaceholder = (dateStr?: Nullable<string>) => {
  return dateStr
    ? dayjs(dateStr).format(DEFAULT_DATETIME_FORMAT)
    : (displayTextOrPlaceholder(null) as string)
}

export const displayFullUTCDateOrPlaceholder = (
  dateStr?: Nullable<string>,
  formatOverride: string = FULL_DATE_TIME_FORMAT
) => {
  return dateStr
    ? formatAsUTCDatetime(dateStr, formatOverride)
    : (displayTextOrPlaceholder(null) as string)
}

export const displayLocalDateTimeOrPlaceholder = (
  dateStr?: Nullable<string>,
  formatOverride?: string
): string => {
  return dateStr
    ? formatLocalDateTime(dateStr, formatOverride)
    : (displayTextOrPlaceholder(null) as string)
}

// Timestamps are stored with "Z" indicating UTC but actually represents/stored as local time
// This basically ignores the "Z" and treat the time as local time
export const getLocalTimestampDayJs = (dateStr?: string): Dayjs => {
  return dayjs(dateStr, 'YYYY-MM-DDTHH:mm:ss.SSS[Z]')
}

export const toTimestampPayload = (dateMom?: Nullable<Dayjs>) => {
  if (!dateMom) {
    return null
  }
  return dateMom.utc(true).toISOString()
}
